/**
 * Storage utility for managing authentication tokens
 * Provides a centralized way to access localStorage for tokens
 * Makes it easier to change storage mechanism in the future and improves testability
 */

const ACCESS_TOKEN_KEY = "accessToken";

/**
 * Get the access token from storage
 * @returns The access token string or null if not found
 */
export const getAccessToken = (): string | null => {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error("Error reading access token from storage:", error);
    return null;
  }
};

/**
 * Set the access token in storage
 * @param token - The access token to store
 */
export const setAccessToken = (token: string): void => {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch (error) {
    console.error("Error storing access token:", error);
    throw new Error("Failed to store access token");
  }
};

/**
 * Remove the access token from storage
 */
export const removeAccessToken = (): void => {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error("Error removing access token from storage:", error);
  }
};

/**
 * Check if an access token exists in storage
 * @returns true if token exists, false otherwise
 */
export const hasAccessToken = (): boolean => {
  return getAccessToken() !== null;
};
